import type { useNavigate } from 'react-router-dom'

/**
 * Read the saved active tab path from localStorage so we can navigate
 * directly to the last-open page instead of falling through to /home/overview.
 */
function getSavedTabPath(): string | null {
  try {
    const activeLifeSpace = localStorage.getItem('activeLifeSpace') || 'personal'
    const sessionStr = localStorage.getItem(`tab_session_${activeLifeSpace}`)
    if (!sessionStr) return null

    const session = JSON.parse(sessionStr)

    // Check expiry (1 week — mirrors useTabPersistence)
    const ONE_WEEK = 7 * 24 * 60 * 60 * 1000
    if (Date.now() - session.timestamp > ONE_WEEK) return null

    // Find the active tab, fall back to first tab
    const activeTab =
      session.tabs?.find((t: { id: string }) => t.id === session.activeTabId) ||
      session.tabs?.[0]

    if (!activeTab?.path) return null

    // Only use paths that are under /home/
    if (!activeTab.path.startsWith('/home/')) return null

    return activeTab.path
  } catch {
    return null
  }
}

/**
 * Navigate to /home using IPC transition if in Electron (creates proper Main window),
 * or React Router navigate if already in the correct window or on web.
 *
 * Restores the last active tab path from localStorage to avoid a race condition
 * where HomeLayout's `<Navigate to="overview" />` redirect fires before
 * useTabManager can restore the saved tab.
 */
export async function navigateToHome(navigate: ReturnType<typeof useNavigate>) {
  const targetPath = getSavedTabPath() || '/home/overview'

  if (window.api?.invoke) {
    try {
      const initProps = (await window.api.invoke('init-window')) as { windowType?: string } | null
      if (initProps?.windowType === 'main') {
        navigate(targetPath, { replace: true })
      } else {
        await window.api.invoke('transition-to-main')
      }
    } catch (_error) {
      navigate(targetPath, { replace: true })
    }
  } else {
    navigate(targetPath, { replace: true })
  }
}
